/**
 * Achievement Helpers
 * Utility functions for working with achievements
 */

import {
  Achievement,
  BaseAchievement,
  TieredAchievement,
  AchievementData,
  AchievementDataItem,
  AchievementId,
  AchievementLevel
} from '../types/achievementTypes'

/**
 * Check if an achievement is completed
 * @param achievement - Achievement to check
 * @returns True if achievement is completed
 * For tiered achievements, only returns true if user has reached Gold level (maximum tier)
 */
export function isAchievementCompleted(achievement: Achievement): boolean {
  if (achievement.type === 'simple') {
    return (achievement as BaseAchievement).isCompleted
  }

  const tieredAchievement = achievement as TieredAchievement
  return tieredAchievement.level === AchievementLevel.GOLD
}

/**
 * Format achievement value for display
 * @param value - Achievement value (number or boolean)
 * @param extraValue - Optional extra value
 * @returns Formatted string
 */
export function formatAchievementValue(value: number | boolean, extraValue?: number): string {
  if (typeof value === 'boolean') {
    return value ? 'Completed' : 'Not Started'
  }

  if (extraValue !== undefined) {
    return `${value} (${extraValue})`
  }

  return value.toString()
}

/**
 * Get achievement level text for display
 * @param achievement - Achievement to get level text for
 * @returns Level text string
 */
export function getAchievementLevelText(achievement: Achievement): string {
  if (achievement.type === 'simple') {
    return (achievement as BaseAchievement).isCompleted ? 'Achieved' : 'Not Started'
  }

  const { level, nextLevel, thresholds } = achievement as TieredAchievement

  if (!level) {
    return `Next: Bronze (${thresholds.Bronze})`
  }

  if (!nextLevel) {
    return 'MAX LEVEL'
  }

  return `LEVEL ${level.toUpperCase()} (Next: ${nextLevel.toUpperCase()} at ${thresholds[nextLevel]})`
}

/**
 * Validate achievement data structure
 * @param data - Data to validate
 * @returns True if valid
 */
export function validateAchievementData(data: unknown): data is AchievementData {
  if (!data || typeof data !== 'object') {
    return false
  }

  const record = data as Record<string, unknown>

  for (const [key, value] of Object.entries(record)) {
    // Check if key is a valid AchievementId
    if (!Object.values(AchievementId).includes(key as AchievementId)) {
      continue // Skip unknown keys, don't fail validation
    }

    // Check if value is an object with value property
    if (!value || typeof value !== 'object') {
      return false
    }

    const item = value as AchievementDataItem

    // Check if value exists and is a valid type (boolean or number)
    if (item.value === undefined || item.value === null) {
      return false
    }

    if (typeof item.value !== 'boolean' && typeof item.value !== 'number') {
      return false
    }

    // Check if extraValue is a number if provided
    if (item.extraValue !== undefined && typeof item.extraValue !== 'number') {
      return false
    }
  }

  return true
}
